"use client";

import { forwardRef, useCallback, useEffect, useState, type ReactNode } from "react";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useDragScroll } from "@/hooks/useDragScroll";

/** 드롭다운 안의 한 줄 — 아이콘 타일 + 제목 + 보조 문구 */
export function PanelRow({
  icon,
  title,
  sub,
  right,
  onClick,
  active = false,
  accent = false,
}: {
  icon: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  onClick: () => void;
  active?: boolean;
  /** 주황 강조(내 주변 등 바로가기) — 아이콘 타일·제목을 브랜드 주황으로 */
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-gray-50",
        active && "bg-gray-50",
      )}
    >
      <span
        className={cn(
          "flex size-12 shrink-0 items-center justify-center rounded-xl [&_svg]:size-5.5",
          accent ? "bg-orange-50 text-orange-600" : "bg-gray-100 text-gray-800",
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-[15px]", accent ? "font-semibold text-orange-600" : "text-gray-900")}>
          {title}
        </span>
        {sub && <span className="mt-0.5 block truncate text-[13px] text-gray-500">{sub}</span>}
      </span>
      {right}
    </button>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="mb-1.5 mt-4 px-2 text-xs font-semibold text-gray-500 first:mt-0">{children}</p>;
}

/** 이용 방식 탭(현장 수령 / 숙소 수령 / 국내 배송) — 회색 레일 위 흰 알약 */
export function SegmentTabs<T extends string>({
  value,
  options,
  onChange,
  full = false,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  full?: boolean;
}) {
  return (
    <div
      role="tablist"
      className={cn("flex gap-1 rounded-full bg-gray-100 p-1", full ? "w-full" : "w-max")}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-full px-4 py-2 text-sm transition-colors",
            full && "flex-1 px-0",
            value === o.value
              ? "bg-white font-semibold text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-800",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** 드롭다운 맨 위 검색칸 */
export const PanelInput = forwardRef<
  HTMLInputElement,
  {
    value: string;
    placeholder: string;
    onChange: (v: string) => void;
    onEnter?: () => void;
    autoFocus?: boolean;
  }
>(function PanelInput({ value, placeholder, onChange, onEnter, autoFocus }, ref) {
  return (
    <label className="flex h-12 items-center gap-2.5 rounded-xl border border-gray-300 px-4 focus-within:border-gray-900">
      <MagnifyingGlassIcon className="size-4.5 shrink-0 text-gray-900" weight="bold" />
      <input
        ref={ref}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            onEnter?.();
          }
        }}
        placeholder={placeholder}
        enterKeyHint="search"
        className="min-w-0 flex-1 bg-transparent text-[15px] text-gray-900 outline-none placeholder:text-gray-400"
      />
      {value && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => onChange("")}
          className="flex size-6 items-center justify-center rounded-full bg-gray-200 text-gray-600"
        >
          <XIcon className="size-3" weight="bold" />
        </button>
      )}
    </label>
  );
});

/**
 * 가로 스크롤 줄 — 데스크톱은 마우스로 끌어 넘긴다(터치는 원래 스크롤). 끌고 놓았을 때는
 * 안쪽 버튼 클릭을 삼킨다. 더 넘길 쪽 가장자리에만 블러 페이드를 켠다(EdgeFadeScroller 와
 * 같은 모양). 드롭다운이 열릴 때 마운트되므로 훅을 이 컴포넌트가 가진다.
 *
 * 음수 마진(-mx-*)으로 부모 패딩을 상쇄할 때는 `wrapperClassName` 에 줘야 페이드가 실제
 * 스크롤 가장자리에 붙는다.
 */
export function DragScrollRow({
  className,
  wrapperClassName,
  children,
}: {
  className?: string;
  wrapperClassName?: string;
  children: ReactNode;
}) {
  const { ref, didDrag } = useDragScroll<HTMLDivElement>();
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 6;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 6;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // observe() 직후 콜백이 1회 오므로 초기 측정도 이 콜백이 한다(effect 본문 setState 없음).
    // 내용(자식)도 관찰한다 — 이미지·폰트가 늦게 붙어 내용 폭만 늘면 상자 콜백이 오지
    // 않아 첫 로드에 오른쪽 페이드가 꺼진 채 남는다(EdgeFadeScroller 와 같은 수정, 10-01).
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    document.fonts?.ready.then(measure).catch(() => {});
    return () => observer.disconnect();
  }, [ref, measure]);

  const fade =
    "pointer-events-none absolute inset-y-0 w-10 transition-opacity duration-200 backdrop-blur-[3px]";
  return (
    <div className={cn("relative", wrapperClassName)}>
      <div
        ref={ref}
        onScroll={measure}
        className={cn("flex cursor-grab select-none overflow-x-auto [scrollbar-width:none]", className)}
        onClickCapture={(e) => {
          if (didDrag()) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          fade,
          "left-0 bg-linear-to-r from-white/95 to-transparent [mask-image:linear-gradient(to_right,#000,transparent)]",
          edges.left ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={cn(
          fade,
          "right-0 bg-linear-to-l from-white/95 to-transparent [mask-image:linear-gradient(to_left,#000,transparent)]",
          edges.right ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}
